import { DateTime } from '@ksyos/date-time';
import { useEffect, useState } from 'react';
import type { IDiagnosticReportGetOne } from '@frontend/components/interfaces';
import { getDisplayName } from '@common/utils/getDisplayName';
import { AddendumRead } from '@frontend/components/elements/AddendumRead';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortableFetch } from '@frontend/utils';

interface ITfoV1DiagnosticReportAddendumReadProps {
    diagnosticReport: IDiagnosticReportGetOne;
}

export function TfoV1DiagnosticReportAddendumRead(props: Readonly<ITfoV1DiagnosticReportAddendumReadProps>) {
    const abortableFetch = useAbortableFetch();
    const translate = getSelectedLanguage();

    const { diagnosticReport } = props;
    const { customControlData } = diagnosticReport;

    const [practitionerProfession, setPractitionerProfession] = useState('');

    useEffect(() => {
        handleOnPerformerPractitionerIdChange();
    }, [diagnosticReport.performerPractitionerId]);

    const properties = [
        {
            key: 'retinopathie_od',
            label: `${translate.ophthalmology.retinopathie} OD`,
            value: customControlData.addendum.properties.retinopathie_od,
        },
        {
            key: 'retinopathie_os',
            label: `${translate.ophthalmology.retinopathie} OS`,
            value: customControlData.addendum.properties.retinopathie_os,
        },
        {
            key: 'maculopathie_od',
            label: `${translate.ophthalmology.maculopathie} OD`,
            value: customControlData.addendum.properties.maculopathie_od,
        },
        {
            key: 'maculopathie_os',
            label: `${translate.ophthalmology.maculopathie} OS`,
            value: customControlData.addendum.properties.maculopathie_os,
        },
        {
            key: 'andere_bevindigen_od',
            label: `${translate.ophthalmology.andere_bevindingen} OD`,
            value: renderYesOrNo(customControlData.addendum.properties.andere_bevindingen_od),
        },
        {
            key: 'andere_bevindingen_od_text',
            label: `${translate.ophthalmology.andere_bevindingen} OD`,
            value: customControlData.addendum.properties.andere_bevindingen_od_text,
        },
        {
            key: 'andere_bevindingen_os',
            label: `${translate.ophthalmology.andere_bevindingen} OS`,
            value: renderYesOrNo(customControlData.addendum.properties.andere_bevindingen_os),
        },
        {
            key: 'andere_bevindingen_os_text',
            label: `${translate.ophthalmology.andere_bevindingen} OS`,
            value: customControlData.addendum.properties.andere_bevindingen_os_text,
        },
        {
            key: 'geadviseerd_beleid',
            label: translate.ophthalmology.geadviseerd_beleid,
            value: customControlData.addendum.properties.geadviseerd_beleid,
        },
        {
            key: 'geadviseerd_beleid_anders',
            label: translate.common.other,
            value: customControlData.addendum.properties.geadviseerd_beleid_anders,
        },
        {
            key: 'urgentie',
            label: translate.ophthalmology.urgentie,
            value:
                customControlData.addendum.properties.urgentie !== undefined
                    ? (customControlData.addendum.properties.urgentie ?? '(Geen urgentie)')
                    : undefined,
        },
    ];

    const addendum = {
        createdAt: DateTime.from(customControlData.addendum.createdAt),
        practitionerName: diagnosticReport.performerPractitioner
            ? getDisplayName(diagnosticReport.performerPractitioner)
            : '',
        practitionerProfession,
        properties,
    };

    return <AddendumRead value={addendum} />;

    async function handleOnPerformerPractitionerIdChange() {
        const response = await abortableFetch.getJson(
            `/api/employments?practitionerId=${diagnosticReport.performerPractitionerId}&organizationId=${diagnosticReport.performerOrganizationId}&fields=profession`
        );

        const profession = response.json.employments[0]?.profession;

        if (profession) {
            setPractitionerProfession(translate.professionEnum[profession as keyof typeof translate.professionEnum]);
        }
    }

    function renderYesOrNo(value: boolean) {
        if (value !== undefined) {
            return value ? translate.common.yes : translate.common.no;
        }
    }
}
